// Pure merge helper: combines the producer content tree with the revenue map
// into a uniform, render-friendly view model. No MobX / React dependencies.
//
// The API serializes camelCase (Baasic REST convention): the content tree is
// { collections: [...], albums: [...] } with node fields { id, title, contentType,
// albums, tracks, revenuePercentage }, and revenue rows are
// { level, id, totalRevenue, producerPayment }.
//
//   totalRevenue:    gross earnings for the content item across all producers (the real stored value)
//   producerPayment: this producer's net share (totalRevenue × their ownership %)
//
// Each node also carries cover info so the UI can show a thumbnail via CoverImageFactory:
//   coverKind:    "artist" | "album" | "podcast" (which CoverImageFactory method to use)
//   coverImageId: the cover image file-entry id passed to that factory method. Tracks inherit
//                 their album's coverImageId; episodes inherit their podcast's.

function indexRevenueByLevel(revenueMap) {
    // Keys are lowercased to normalise the API's `level` discriminator
    // (so "podcastGroup" indexes as "podcastgroup").
    const index = { collection: {}, album: {}, track: {}, podcastgroup: {}, podcast: {}, episode: {} };
    if (!Array.isArray(revenueMap)) {
        return index;
    }
    for (const entry of revenueMap) {
        if (!entry || !entry.level || entry.id == null) {
            continue;
        }
        const level = String(entry.level).toLowerCase();
        if (index[level]) {
            // totalRevenue falls back to producerPayment for older API responses that lack the field.
            index[level][entry.id] = {
                producerPayment: entry.producerPayment,
                totalRevenue: entry.totalRevenue != null ? entry.totalRevenue : entry.producerPayment,
            };
        }
    }
    return index;
}

function buildTrackNode(track, albumId, albumCoverImageId, trackRevenue, linkable) {
    const entry = Object.prototype.hasOwnProperty.call(trackRevenue, track.id) ? trackRevenue[track.id] : null;
    return {
        id: track.id,
        title: track.title,
        type: "track",
        revenue: entry ? entry.producerPayment : null,
        totalRevenue: entry ? entry.totalRevenue : null,
        revenueMatched: entry !== null,
        revenuePercentage: track.revenuePercentage != null ? track.revenuePercentage : null,
        coverKind: "album",
        // Tracks have no cover of their own; they reuse the parent album's cover.
        coverImageId: albumCoverImageId,
        // Parent album id — needed to build the track's player link.
        albumId,
        // Tracks have no enabled flag of their own; they inherit their album's effective state.
        linkable,
        children: [],
    };
}

// `ancestorLinkable` is the parent's effective link state. The copy link is offered only
// when the node AND all its ancestors are enabled and not female content, so a disabled or
// female artist/album hides the link on itself and on every album/track beneath it.
function buildAlbumNode(album, index, ancestorLinkable) {
    const entry = Object.prototype.hasOwnProperty.call(index.album, album.id) ? index.album[album.id] : null;
    // Default missing isActive to enabled, so older API responses are never hidden.
    const active = album.isActive !== false;
    // Albums carry no isFemale of their own; they inherit their artist's through ancestorLinkable.
    const linkable = ancestorLinkable && active;
    return {
        id: album.id,
        title: album.title,
        type: "album",
        revenue: entry ? entry.producerPayment : 0,
        totalRevenue: entry ? entry.totalRevenue : 0,
        revenueMatched: true,
        revenuePercentage: album.revenuePercentage != null ? album.revenuePercentage : null,
        isActive: active,
        linkable,
        coverKind: "album",
        coverImageId: album.coverImageId,
        children: (album.tracks || []).map((track) =>
            buildTrackNode(track, album.id, album.coverImageId, index.track, linkable)
        ),
    };
}

function buildCollectionNode(collection, index) {
    const entry = Object.prototype.hasOwnProperty.call(index.collection, collection.id) ? index.collection[collection.id] : null;
    const active = collection.isActive !== false;
    // Female content is not publicly shareable (#160843), so it is offered no copy link here or on
    // anything beneath it. The content itself stays listed and manageable.
    const linkable = active && collection.isFemale !== true;
    return {
        id: collection.id,
        title: collection.title,
        type: "collection",
        revenue: entry ? entry.producerPayment : 0,
        totalRevenue: entry ? entry.totalRevenue : 0,
        revenueMatched: true,
        revenuePercentage: collection.revenuePercentage != null ? collection.revenuePercentage : null,
        isActive: active,
        linkable,
        coverKind: "artist",
        coverImageId: collection.coverImageId,
        children: (collection.albums || [])
            .map((album) => buildAlbumNode(album, index, linkable))
            .filter(isContentVisible),
    };
}

// Visibility rule: disabled artists/albums are hidden ONLY when they earned nothing.
// Enabled content is always shown; content that earned anything is always shown.
function earnedSomething(node) {
    return typeof node.revenue === "number" && node.revenue > 0;
}

function isContentVisible(node) {
    return node.isActive || earnedSomething(node);
}

// A collection is shown when it qualifies on its own (enabled or earned) AND still has at
// least one visible album. This hides a disabled, unearning artist, and also drops an artist
// left with no visible albums after its albums were filtered.
function isCollectionVisible(node) {
    return isContentVisible(node) && node.children.length > 0;
}

function buildEpisodeNode(episode, podcastId, categorySlug, podcastCoverImageId, episodeRevenue, linkable) {
    const entry = Object.prototype.hasOwnProperty.call(episodeRevenue, episode.id) ? episodeRevenue[episode.id] : null;
    return {
        id: episode.id,
        title: episode.title,
        type: "episode",
        revenue: entry ? entry.producerPayment : null,
        totalRevenue: entry ? entry.totalRevenue : null,
        revenueMatched: entry !== null,
        revenuePercentage: 100,
        coverKind: "podcast",
        // Episodes have no cover of their own; they reuse the parent podcast's cover.
        coverImageId: podcastCoverImageId,
        // Parent podcast id + its category slug — needed to build the episode link.
        podcastId,
        categorySlug,
        // Episodes have no enabled flag of their own; they inherit their podcast's effective state.
        linkable,
        children: [],
    };
}

function buildPodcastNode(podcast, index, ancestorLinkable) {
    const entry = Object.prototype.hasOwnProperty.call(index.podcast, podcast.id) ? index.podcast[podcast.id] : null;
    const active = podcast.isActive !== false;
    // Unlike albums, a podcast carries its own isFemale — groups, standalone podcasts and sub-podcasts
    // share one table, and the marker sits on the individual podcast. Episodes inherit it from here.
    const linkable = ancestorLinkable && active && podcast.isFemale !== true;
    return {
        id: podcast.id,
        title: podcast.title,
        type: "podcast",
        revenue: entry ? entry.producerPayment : 0,
        totalRevenue: entry ? entry.totalRevenue : 0,
        revenueMatched: true,
        revenuePercentage: 100,
        isActive: active,
        linkable,
        coverKind: "podcast",
        coverImageId: podcast.coverImageId,
        // Category slug ("podcast" | "story" | "shiur") — needed to build the podcast link.
        categorySlug: podcast.categorySlug,
        children: (podcast.episodes || []).map((episode) =>
            buildEpisodeNode(episode, podcast.id, podcast.categorySlug, podcast.coverImageId, index.episode, linkable)
        ),
    };
}

function buildPodcastGroupNode(group, index) {
    const entry = Object.prototype.hasOwnProperty.call(index.podcastgroup, group.id) ? index.podcastgroup[group.id] : null;
    const active = group.isActive !== false;
    // A group has no marker of its own; the API derives isFemale from its children and sets it only
    // when every one of them is female, which is exactly when the public group page stops resolving.
    // Children are NOT gated on it — a mixed group keeps its link and so do its non-female podcasts.
    const linkable = active && group.isFemale !== true;
    return {
        id: group.id,
        title: group.title,
        type: "podcastGroup",
        revenue: entry ? entry.producerPayment : 0,
        totalRevenue: entry ? entry.totalRevenue : 0,
        revenueMatched: true,
        revenuePercentage: 100,
        isActive: active,
        linkable,
        coverKind: "podcast",
        coverImageId: group.coverImageId,
        categorySlug: group.categorySlug,
        children: (group.podcasts || []).map((podcast) => buildPodcastNode(podcast, index, active)),
    };
}

export default function buildContentTreeWithRevenue(tree, revenueMap) {
    const safeTree = tree || {};
    const index = indexRevenueByLevel(revenueMap);
    const collections = (safeTree.collections || [])
        .map((collection) => buildCollectionNode(collection, index))
        .filter(isCollectionVisible);
    const albums = (safeTree.albums || [])
        .map((album) => buildAlbumNode(album, index, true))
        .filter(isContentVisible);
    const podcastGroups = (safeTree.podcastGroups || []).map((group) =>
        buildPodcastGroupNode(group, index)
    );
    const podcasts = (safeTree.podcasts || []).map((podcast) =>
        buildPodcastNode(podcast, index, true)
    );
    return [...collections, ...albums, ...podcastGroups, ...podcasts];
}
